import { ref, computed } from '@vue/composition-api';
import provider from '@/api/apollo-client';
import schemas from './schemas.graphql';
import useErrors from '@/api/errors';
const { error } = useErrors();

const user = ref(null);
const profile = ref(null);
const loading = ref(false);

export default function(ctx) {
  const token = ref(null);
  const userId = () => localStorage.getItem('userId');

  token.value = localStorage.getItem('authToken');

  function login(input) {
    return provider
      .mutate({
        mutation: schemas.getRefreshToken,
        variables: { input: { ...input } }
      })
      .then(r => {
        const refreshToken = r.data.authRefreshToken.refreshToken?.token;
        const twoFa = r.data.authRefreshToken.refreshToken?.twoFa?.type;

        if (!refreshToken) {
          return Promise.reject(new Error('Login or password incorrect'));
        }

        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem(
          'userId',
          r.data.authRefreshToken.refreshToken?.userId
        );
        localStorage.setItem(
          'tokenId',
          r.data.authRefreshToken.refreshToken.id
        );

        if (twoFa === 'none') {
          return provider.mutate({
            mutation: schemas.getAccessToken,
            variables: {
              input: {
                userRefreshToken: localStorage.getItem('refreshToken'),
                accessTokenExpiration: 24 * 60,
                profileTags: ['application', 'admin', 'user profile']
              }
            },
            fetchPolicy: 'no-cache'
          });
        } else {
          return Promise.resolve({
            twoFA: r.data.authRefreshToken.refreshToken?.twoFa
          });
        }
      })
      .then(async r => {
        if (r.twoFA) {
          return Promise.resolve(r);
        }

        const accessToken = r.data.authAccessToken.jwtToken;

        if (accessToken) {
          localStorage.setItem('authToken', accessToken);
          token.value = accessToken;
          user.value = { id: 1 };
          await loadUser();
        } else {
          return Promise.reject(new Error('User does not exist'));
        }
      })
      .catch(e => {
        return Promise.reject(e);
      });
  }

  function submitOTP(otp) {
    return provider
      .mutate({
        mutation: schemas.getAccessToken,
        variables: {
          input: {
            userRefreshToken: localStorage.getItem('refreshToken'),
            accessTokenExpiration: 24 * 60,
            profileTags: ['application', 'admin', 'user profile'],
            otp
          }
        },
        fetchPolicy: 'no-cache'
      })
      .then(async r => {
        const accessToken = r.data.authAccessToken.jwtToken;

        if (accessToken) {
          localStorage.setItem('authToken', accessToken);
          token.value = accessToken;
          user.value = { id: 1 };
          await loadUser();
        } else {
          return Promise.reject(new Error('User does not exist'));
        }
      })
      .catch(e => {
        return Promise.reject(e);
      });
  }

  function loginFromApp() {
    const refreshToken = localStorage.getItem('refreshToken');
    return provider
      .mutate({
        mutation: schemas.getAccessToken,
        variables: {
          input: {
            userRefreshToken: refreshToken,
            accessTokenExpiration: 24 * 60,
            profileTags: ['application', 'admin', 'user profile']
          }
        }
      })
      .then(r => {
        const jwt = r.data.authAccessToken.jwtToken;
        if (jwt) {
          localStorage.setItem('authToken', jwt);
          token.value = jwt;
          user.value = { id: 1 };
        } else {
          return Promise.reject(new Error('User does not exist'));
        }
      })
      .then(() => {
        loadUser();
      })
      .catch(e => {
        throw new Error(e);
      });
  }

  function logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('tokenId');
    token.value = null;
    user.value = null;
  }

  function getUserId() {
    return provider
      .query({
        query: schemas.getUserId
      })
      .then(r => r.data.getUserId)
      .catch(e => {
        return Promise.reject(e);
      });
  }

  function getRefreshTokenId() {
    return provider.query({
      query: schemas.getRefreshTokenId,
      fetchPolicy: 'no-cache'
    });
  }

  function getUserProfileId() {
    return provider
      .query({
        query: schemas.getUserProfileId,
        fetchPolicy: 'no-cache'
      })
      .then(r => {
        if (r.data.getUserProfileId) {
          return r.data.getUserProfileId;
        }
        throw new Error('getUserProfileId does not exist');
      })
      .catch(e => {
        return Promise.reject(e);
      });
  }

  function loadProfile() {
    return getUserProfileId()
      .then(id => {
        return provider.query({
          query: schemas.loadProfile,
          variables: {
            id
          },
          fetchPolicy: 'no-cache'
        });
      })
      .then(r => {
        profile.value = r.data.object;
      })
      .catch(e => {
        error.value = e;
        return Promise.reject(e);
      });
  }

  async function loadProfileAsync() {
    await getUserProfileId()
      .then(id => {
        return provider.query({
          query: schemas.loadProfile,
          variables: {
            id
          },
          fetchPolicy: 'no-cache'
        });
      })
      .then(r => {
        profile.value = r.data.object;
      })
      .catch(e => {
        error.value = e;
        return Promise.reject(e);
      });
  }

  function getVersion() {
    return provider.query({
      query: schemas.getVersion,
      fetchPolicy: 'no-cache'
    });
  }

  function getTimezoneShift() {
    return provider.query({
      query: schemas.getTimezoneShift,
      fetchPolicy: 'no-cache'
    });
  }

  function getDatabaseInfo() {
    return provider.query({
      query: schemas.getDbInfo,
      fetchPolicy: 'no-cache'
    });
  }

  function getTimezoneInfo() {
    return provider.query({
      query: schemas.getTimezoneInfo,
      fetchPolicy: 'no-cache'
    });
  }

  function loadUser() {
    if (loading.value === false) {
      loading.value = true;
      return provider
        .query({
          query: schemas.loadUser,
          fetchPolicy: 'no-cache',
          variables: {
            id: userId()
          }
        })
        .then(r => {
          user.value = r.data.user;
          return r.data.user;
        })
        .catch(e => {
          return Promise.reject(e);
        })
        .finally(() => {
          loading.value = false;
        });
    }
  }

  const isLoggedIn = computed(() => !!user.value);

  return {
    user,
    loading,
    loadUser,
    loadProfile,
    getUserId,
    token,
    login,
    submitOTP,
    logout,
    getVersion,
    getDatabaseInfo,
    getUserProfileId,
    getRefreshTokenId,
    loginFromApp,
    isLoggedIn,
    profile,
    loadProfileAsync,
    getTimezoneInfo,
    getTimezoneShift
  };
}
